import { atom } from 'jotai';

import { tipsService } from '../services';
import { toast } from 'sonner';
import { TipResponse, TipStatus } from '@/types/tips';

// Core state atoms
export const tipsAtom = atom<TipResponse[]>([]);
export const tipsLoadingAtom = atom(false);
export const tipsErrorAtom = atom<string | null>(null);

// Download state atoms
export const attachmentDownloadLoadingAtom = atom<string | null>(null);
export const attachmentDownloadErrorAtom = atom<string | null>(null);

// Filter atoms
export const tipsFilterAtom = atom<'all' | TipStatus>('all');
export const tipsSearchQueryAtom = atom('');

// Derived atom for filtered tips
export const filteredTipsAtom = atom(get => {
  const tips = get(tipsAtom);
  const filter = get(tipsFilterAtom);
  const searchQuery = get(tipsSearchQueryAtom);

  let filtered = tips;

  // Apply status filter
  if (filter !== 'all') {
    filtered = filtered.filter(tip => tip.status === filter);
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(
      tip =>
        tip.title.toLowerCase().includes(query) ||
        (tip.description && tip.description.toLowerCase().includes(query)) ||
        (tip.sourceContact && tip.sourceContact.toLowerCase().includes(query))
    );
  }

  return filtered;
});

// Async action to fetch tips
export const fetchTipsActionAtom = atom(null, async (_get, set) => {
  try {
    set(tipsLoadingAtom, true);
    set(tipsErrorAtom, null);

    // Always fetch all tips - filtering is done client-side via filteredTipsAtom
    const tips = await tipsService.getTips();

    set(tipsAtom, tips);
    return tips;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch tips';
    set(tipsErrorAtom, errorMessage);
    console.error('Error fetching tips:', error);
    throw error;
  } finally {
    set(tipsLoadingAtom, false);
  }
});

// Fetch single tip by ID
export const fetchTipByIdActionAtom = atom(
  null,
  async (get, set, tipId: string) => {
    try {
      set(tipsLoadingAtom, true);
      set(tipsErrorAtom, null);

      const tip = await tipsService.getTip(tipId);

      const currentTips = get(tipsAtom);
      const updatedTips = [...currentTips.filter(t => t.id !== tipId), tip];
      set(tipsAtom, updatedTips);

      return tip;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch tip by ID';
      set(tipsErrorAtom, message);
      throw error;
    } finally {
      set(tipsLoadingAtom, false);
    }
  }
);

// Action to approve a tip
export const approveTipActionAtom = atom(
  null,
  async (get, set, tipId: string) => {
    try {
      set(tipsLoadingAtom, true);
      set(tipsErrorAtom, null);

      const updatedTip = await tipsService.approveTip(tipId);

      // Update the tip in the current list
      const currentTips = get(tipsAtom);
      const updatedTips = currentTips.map(tip =>
        tip.id === tipId ? updatedTip : tip
      );
      set(tipsAtom, updatedTips);

      toast.success('Tip approved successfully');
      return updatedTip;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to approve tip';
      set(tipsErrorAtom, errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      set(tipsLoadingAtom, false);
    }
  }
);

// Action to reject a tip
export const rejectTipActionAtom = atom(
  null,
  async (get, set, tipId: string) => {
    try {
      set(tipsLoadingAtom, true);
      set(tipsErrorAtom, null);

      const updatedTip = await tipsService.rejectTip(tipId);

      // Update the tip in the current list
      const currentTips = get(tipsAtom);
      const updatedTips = currentTips.map(tip =>
        tip.id === tipId ? updatedTip : tip
      );
      set(tipsAtom, updatedTips);
      toast.success('Tip rejected successfully');

      return updatedTip;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to reject tip';
      set(tipsErrorAtom, errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      set(tipsLoadingAtom, false);
    }
  }
);

// Action to create a tip
export const createTipActionAtom = atom(
  null,
  async (get, set, tipData: import('../types/api').TipCreate) => {
    try {
      set(tipsLoadingAtom, true);
      set(tipsErrorAtom, null);

      const newTip = await tipsService.createTip(tipData);

      // Add the new tip to the current list
      const currentTips = get(tipsAtom);
      const updatedTips = [newTip, ...currentTips];
      set(tipsAtom, updatedTips);
      toast.success('Tip created successfully');
      return newTip;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create tip';
      set(tipsErrorAtom, errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      set(tipsLoadingAtom, false);
    }
  }
);

// Action to update a tip
export const updateTipActionAtom = atom(
  null,
  async (
    get,
    set,
    tipId: string,
    tipData: import('../types/api').TipUpdate
  ) => {
    try {
      set(tipsLoadingAtom, true);
      set(tipsErrorAtom, null);

      const updatedTip = await tipsService.updateTip(tipId, tipData);

      // Update the tip in the current list
      const currentTips = get(tipsAtom);
      const updatedTips = currentTips.map(tip =>
        tip.id === tipId ? updatedTip : tip
      );
      set(tipsAtom, updatedTips);
      toast.success('Tip updated successfully');
      return updatedTip;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update tip';
      set(tipsErrorAtom, errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      set(tipsLoadingAtom, false);
    }
  }
);

// Action to delete a tip
export const deleteTipActionAtom = atom(
  null,
  async (get, set, tipId: string) => {
    try {
      set(tipsLoadingAtom, true);
      set(tipsErrorAtom, null);

      await tipsService.deleteTip(tipId);

      // Remove the tip from the current list
      const currentTips = get(tipsAtom);
      const updatedTips = currentTips.filter(tip => tip.id !== tipId);
      set(tipsAtom, updatedTips);
      toast.success('Tip deleted successfully');

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete tip';
      set(tipsErrorAtom, errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      set(tipsLoadingAtom, false);
    }
  }
);

// IMAP Ingestor Action
export const triggerImapIngestActionAtom = atom(null, async (_get, set) => {
  try {
    set(tipsLoadingAtom, true);
    set(tipsErrorAtom, null);

    const result = await tipsService.triggerImapIngest();

    // Refresh tips after ingestion
    await set(fetchTipsActionAtom);
    toast.success('IMAP ingest triggered successfully');
    return result;
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to trigger IMAP ingestion';
    set(tipsErrorAtom, errorMessage);
    toast.error(errorMessage);
    return null;
  } finally {
    set(tipsLoadingAtom, false);
  }
});

// Action to download attachment
export const downloadAttachmentActionAtom = atom(
  null,
  async (
    get,
    set,
    {
      tipId,
      s3Key,
      filename,
    }: { tipId: string; s3Key: string; filename: string }
  ) => {
    const currentlyDownloading = get(attachmentDownloadLoadingAtom);
    if (currentlyDownloading) {
      toast.info('Please wait for current download to complete');
      return;
    }

    try {
      set(attachmentDownloadLoadingAtom, s3Key);
      set(attachmentDownloadErrorAtom, null);

      toast.info(`Downloading ${filename}...`);

      const { url: downloadUrl } = await tipsService.getAttachmentDownloadUrl(
        tipId,
        s3Key
      );

      // create link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Successfully downloaded ${filename}`);

      return { success: true, filename };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to download attachment';

      set(attachmentDownloadErrorAtom, errorMessage);
      toast.error(`Failed to download ${filename}: ${errorMessage}`);

      return { success: false, error: errorMessage };
    } finally {
      set(attachmentDownloadLoadingAtom, null);
    }
  }
);

// Stats atoms
export const tipsStatsAtom = atom<Record<string, number>>({});
export const fetchTipsStatsActionAtom = atom(null, async (_get, set) => {
  try {
    const allTips = await tipsService.getTips();
    const stats = {
      total: allTips.length,
      pending: allTips.filter(tip => tip.status === 'pending').length,
      approved: allTips.filter(tip => tip.status === 'approved').length,
      rejected: allTips.filter(tip => tip.status === 'rejected').length,
    };
    set(tipsStatsAtom, stats);
    return stats;
  } catch (error) {
    console.error('Error fetching tips stats:', error);
    // Don't set error for stats, it's not critical
    return {};
  }
});
